import React, { createContext, useContext, useCallback, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { uiConfigService, type UIConfigEntry } from '../services/admin/uiConfigService';
import { queryKeyFactory } from '../services/core/queryKeys';
import { useFeatureFlag } from '../hooks/useFeatureFlags';
import { useTheme } from '../hooks/useTheme';
import {
  DEFAULT_DEVIATION_COLORS,
  DEFAULT_HEADER_COLORS,
} from '../config/uiConfigDefaults';

// ============================================================================
// ТИПЫ
// ============================================================================

interface DeviationColors {
  positive_bg: string;
  positive_text: string;
  negative_bg: string;
  negative_text: string;
}

interface HeaderColors {
  purple: string;
  blue: string;
  orange: string;
  green: string;
}

interface UIConfigContextValue {
  isLoading: boolean;
  isEnabled: boolean;
  getStageTitle: (section: string, fallback: string) => string;
  getStageDescription: (section: string, fallback: string) => string;
  getButtonText: (key: string, fallback: string) => string;
  getHeaders: (section: string, key: string, defaults: string[]) => string[];
  getColumnHeaders: (section: string, defaults: string[]) => string[];
  getHeaderColors: (section: string, key: string) => Map<number, string>;
  getNavLabel: (key: string, fallback: string) => string;
  getNavIcon: (key: string, fallback: string) => string;
  getViewLabels: (section: string, defaults: string[]) => string[];
  getViewTitle: (section: string, viewKey: string, fallback: string) => string;
  getNodeLabel: (section: string, nodeId: string, fallback: string) => string;
  deviationColors: DeviationColors;
  headerColors: HeaderColors;
}

// ============================================================================
// CONTEXT
// ============================================================================

const UIConfigContext = createContext<UIConfigContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export const UIConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isDark } = useTheme();
  const { isEnabled: featureEnabled, isLoading: flagLoading } = useFeatureFlag('ui_customization');

  const configQuery = useQuery<UIConfigEntry[]>({
    queryKey: queryKeyFactory.uiConfig.all,
    queryFn: uiConfigService.getAll,
    enabled: featureEnabled && !flagLoading,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Build config map: "section:key" -> value
  const configMap = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    if (configQuery.data) {
      for (const entry of configQuery.data) {
        map.set(`${entry.section}:${entry.key}`, entry.value);
      }
    }
    return map;
  }, [configQuery.data]);

  // Computed colors with defaults
  const deviationColors = useMemo((): DeviationColors => {
    const config = configMap.get('global:deviation_colors');
    if (!config) return { ...DEFAULT_DEVIATION_COLORS };
    return {
      positive_bg: (config.positive_bg as string) || DEFAULT_DEVIATION_COLORS.positive_bg,
      positive_text: (config.positive_text as string) || DEFAULT_DEVIATION_COLORS.positive_text,
      negative_bg: (config.negative_bg as string) || DEFAULT_DEVIATION_COLORS.negative_bg,
      negative_text: (config.negative_text as string) || DEFAULT_DEVIATION_COLORS.negative_text,
    };
  }, [configMap]);

  const headerColors = useMemo((): HeaderColors => {
    const config = configMap.get('global:header_colors');
    if (!config) return { ...DEFAULT_HEADER_COLORS };
    return {
      purple: (config.purple as string) || DEFAULT_HEADER_COLORS.purple,
      blue: (config.blue as string) || DEFAULT_HEADER_COLORS.blue,
      orange: (config.orange as string) || DEFAULT_HEADER_COLORS.orange,
      green: (config.green as string) || DEFAULT_HEADER_COLORS.green,
    };
  }, [configMap]);

  // CSS variable injection
  // In dark mode, remove inline properties so dark.scss overrides take effect.
  // In light mode, inject config-driven colors as before.
  useEffect(() => {
    const root = document.documentElement.style;
    const deviationVars = [
      'deviation-positive-bg', 'deviation-positive-text',
      'deviation-negative-bg', 'deviation-negative-text',
    ] as const;
    const headerVars = [
      'header-color-purple', 'header-color-blue',
      'header-color-orange', 'header-color-green',
    ] as const;

    if (isDark) {
      for (const v of deviationVars) root.removeProperty(`--${v}`);
      for (const v of headerVars) root.removeProperty(`--${v}`);
    } else {
      root.setProperty('--deviation-positive-bg', deviationColors.positive_bg);
      root.setProperty('--deviation-positive-text', deviationColors.positive_text);
      root.setProperty('--deviation-negative-bg', deviationColors.negative_bg);
      root.setProperty('--deviation-negative-text', deviationColors.negative_text);
      root.setProperty('--header-color-purple', headerColors.purple);
      root.setProperty('--header-color-blue', headerColors.blue);
      root.setProperty('--header-color-orange', headerColors.orange);
      root.setProperty('--header-color-green', headerColors.green);
    }
  }, [deviationColors, headerColors, isDark]);

  // Getter functions with fallbacks
  const getStageTitle = useCallback(
    (section: string, fallback: string): string => {
      const config = configMap.get(`${section}:stage_title`);
      return (config?.text as string) || fallback;
    },
    [configMap],
  );

  const getStageDescription = useCallback(
    (section: string, fallback: string): string => {
      const config = configMap.get(`${section}:stage_description`);
      return (config?.text as string) || fallback;
    },
    [configMap],
  );

  const getButtonText = useCallback(
    (key: string, fallback: string): string => {
      const config = configMap.get('global:button_texts');
      if (!config) return fallback;
      return (config[key] as string) || fallback;
    },
    [configMap],
  );

  const getHeaders = useCallback(
    (section: string, key: string, defaults: string[]): string[] => {
      const config = configMap.get(`${section}:${key}`);
      if (!config) return defaults;
      // Поддержка форматов:
      // 1. { columns: [{label: "a", headerColor: "#..."}, ...] } — основной формат (admin + seed)
      // 2. { headers: ["a", "b"] } — legacy seed format (массив строк)
      // 3. { base_headers: ["a", "b"] } — legacy seed format (массив строк)
      if (Array.isArray(config.columns)) {
        const cols = config.columns as Array<{ label: string; dynamic?: boolean }>;
        const result = cols.map((c, i) =>
          c.dynamic ? ((defaults[i] ?? c.label) ?? '') : (c.label || (defaults[i] ?? '')),
        );
        // If config has fewer columns than defaults (e.g. new columns added), append remaining
        if (result.length < defaults.length) {
          result.push(...defaults.slice(result.length));
        }
        return result;
      }
      const legacyHeaders = config.headers || config.base_headers;
      if (Array.isArray(legacyHeaders)) {
        return (legacyHeaders as string[]).map((h, i) => h || defaults[i]);
      }
      return defaults;
    },
    [configMap],
  );

  const getColumnHeaders = useCallback(
    (section: string, defaults: string[]): string[] => getHeaders(section, 'column_headers', defaults),
    [getHeaders],
  );

  const getHeaderColors = useCallback(
    (section: string, key: string): Map<number, string> => {
      const config = configMap.get(`${section}:${key}`);
      if (!config) return new Map();
      const colors = new Map<number, string>();
      // Support: { columns: [{label, headerColor, dynamic?}, ...] }
      if (Array.isArray(config.columns)) {
        (config.columns as Array<{ headerColor?: string | null; dynamic?: boolean }>).forEach((c, i) => {
          if (c.headerColor) colors.set(i, c.headerColor);
        });
      }
      // Support: { colors: { "0": "#9333ea", "3": "#3b82f6" } } — index-based color map
      if (config.colors && typeof config.colors === 'object') {
        Object.entries(config.colors as Record<string, string>).forEach(([idx, color]) => {
          colors.set(Number(idx), color);
        });
      }
      return colors;
    },
    [configMap],
  );

  const getNavLabel = useCallback(
    (key: string, fallback: string): string => {
      const config = configMap.get('navigation:main_menu');
      return (config?.[key] as string) || fallback;
    },
    [configMap],
  );

  const getNavIcon = useCallback(
    (key: string, fallback: string): string => {
      const config = configMap.get('navigation:icons');
      return (config?.[key] as string) || fallback;
    },
    [configMap],
  );

  const getViewLabels = useCallback(
    (section: string, defaults: string[]): string[] => {
      const config = configMap.get(`${section}:view_labels`);
      if (!config || !Array.isArray(config.labels)) return defaults;
      return (config.labels as string[]).map((label, i) => label || defaults[i]);
    },
    [configMap],
  );

  const getViewTitle = useCallback(
    (section: string, viewKey: string, fallback: string): string => {
      const config = configMap.get(`${section}:view_titles`);
      return (config?.[viewKey] as string) || fallback;
    },
    [configMap],
  );

  const getNodeLabel = useCallback(
    (section: string, nodeId: string, fallback: string): string => {
      const config = configMap.get(`${section}:node_labels`);
      return (config?.[nodeId] as string) || fallback;
    },
    [configMap],
  );

  const value: UIConfigContextValue = useMemo(
    () => ({
      isLoading: featureEnabled ? configQuery.isLoading : false,
      isEnabled: featureEnabled,
      getStageTitle,
      getStageDescription,
      getButtonText,
      getHeaders,
      getColumnHeaders,
      getHeaderColors,
      getNavLabel,
      getNavIcon,
      getViewLabels,
      getViewTitle,
      getNodeLabel,
      deviationColors,
      headerColors,
    }),
    [
      featureEnabled,
      configQuery.isLoading,
      getStageTitle,
      getStageDescription,
      getButtonText,
      getHeaders,
      getColumnHeaders,
      getHeaderColors,
      getNavLabel,
      getNavIcon,
      getViewLabels,
      getViewTitle,
      getNodeLabel,
      deviationColors,
      headerColors,
    ],
  );

  return <UIConfigContext.Provider value={value}>{children}</UIConfigContext.Provider>;
};

// ============================================================================
// HOOK
// ============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export function useUIConfig(): UIConfigContextValue {
  const context = useContext(UIConfigContext);
  if (!context) {
    throw new Error('useUIConfig must be used within UIConfigProvider');
  }
  return context;
}
